import { supabase } from '../supabaseClient';
import {
  emptyPointItemNoteDoc,
  type PointItemNoteDoc,
} from '../types/pointItemNote';
import {
  loadPointItemNoteCache,
  loadPointItemNoteLoadedGroupIds,
  markPointItemNoteGroupLoaded,
  normalizePointItemNoteDoc,
  savePointItemNoteCache,
  savePointItemNoteLoadedGroupIds,
} from '../utils/pointItemNoteStorage';
import {
  publishPointItemNoteDoc,
  resolvePointItemNoteFromCacheOrMainTab,
} from '../utils/pointItemNoteTabBridge';
import { isMainPresentationBrowserTab } from '../utils/noteDocTabBridgeCore';
import { invalidateSchoolScopedDataCaches, notifySchoolProgramMutation } from '../utils/schoolDataCacheVersion';
import {
  loadMemberCoinAndItemsForGroup,
  loadMemberPointsForGroup,
  replaceMemberCoinAndItemsForMembers,
  replaceMemberPointDataForMembers,
} from './classMemberOwnedDataService';
import type { GroupMemberPoints, ItemCardSet } from '../types/pointItemNote';
import { emptyGroupMemberPoints } from '../types/pointItemNote';

function docId(workspaceId: string, gradeId: string): string {
  return `pin-${workspaceId}-${gradeId}`;
}

/** 동일 그룹 병렬 ensure 중복 fetch 방지 */
const ensureGroupInflight = new Map<string, Promise<PointItemNoteDoc>>();

function ensureGroupKey(workspaceId: string, gradeId: string, groupId: string): string {
  return `${workspaceId}:${gradeId}:${groupId}`;
}

export type PointItemNoteSaveGroup = {
  groupId: string;
  memberIds: string[];
};

/**
 * 과제 관리용: 아이템 카드 설정만 로드 (멤버 이력 테이블 미조회).
 * 전체 캐시가 있으면 그 itemCards만 재사용.
 */
export async function getPointItemNoteSettings(
  workspaceId: string,
  gradeId: string
): Promise<{ itemCards: ItemCardSet; updatedAt: string }> {
  const empty = emptyPointItemNoteDoc(workspaceId, gradeId);
  if (!workspaceId || !gradeId) {
    return { itemCards: empty.itemCards, updatedAt: empty.updatedAt };
  }

  const cached = loadPointItemNoteCache(workspaceId, gradeId);
  if (cached) {
    return { itemCards: cached.itemCards, updatedAt: cached.updatedAt };
  }

  if (isMainPresentationBrowserTab()) {
    const bridged = await resolvePointItemNoteFromCacheOrMainTab(workspaceId, gradeId);
    if (bridged) {
      return { itemCards: bridged.itemCards, updatedAt: bridged.updatedAt };
    }
    console.warn('[메인프레젠·캐시] 포인트설정 캐시 미스', { workspaceId, gradeId });
    return { itemCards: empty.itemCards, updatedAt: empty.updatedAt };
  }

  const { data, error } = await supabase
    .from('point_item_notes')
    .select('payload, updated_at')
    .eq('workspace_id', workspaceId)
    .eq('grade_id', gradeId)
    .maybeSingle();

  if (error) {
    if (/point_item_notes|does not exist|schema cache/i.test(error.message ?? '')) {
      return { itemCards: empty.itemCards, updatedAt: empty.updatedAt };
    }
    throw new Error(error.message);
  }

  if (!data) {
    return { itemCards: empty.itemCards, updatedAt: empty.updatedAt };
  }

  const settingsDoc = normalizePointItemNoteDoc(
    { ...(data.payload as object), updatedAt: data.updated_at },
    workspaceId,
    gradeId
  );
  // 멤버 이력 없이 settings만 반환 — 전체 캐시에 빈 memberPoints를 쓰지 않음
  return {
    itemCards: settingsDoc.itemCards,
    updatedAt: settingsDoc.updatedAt,
  };
}

/** 설정(payload)만 로드. 멤버 이력은 그룹 단위로 ensurePointItemNoteGroup 사용. */
export async function loadPointItemNoteSettingsDoc(
  workspaceId: string,
  gradeId: string
): Promise<PointItemNoteDoc> {
  if (!workspaceId || !gradeId) return emptyPointItemNoteDoc(workspaceId, gradeId);

  const cached = loadPointItemNoteCache(workspaceId, gradeId);
  if (cached) {
    // loadedGroups 메타가 있는 그룹만 재사용. 추정 로드는 하지 않음(과제 이력 누락 방지).
    return cached;
  }

  /* 메인 프레젠: LS/메인 탭 캐시만 — miss여도 REST 금지 */
  if (isMainPresentationBrowserTab()) {
    const bridged = await resolvePointItemNoteFromCacheOrMainTab(workspaceId, gradeId);
    if (bridged) return bridged;
    console.warn('[메인프레젠·캐시] 포인트노트 캐시 미스', { workspaceId, gradeId });
    return emptyPointItemNoteDoc(workspaceId, gradeId);
  }

  const { data, error } = await supabase
    .from('point_item_notes')
    .select('payload, updated_at')
    .eq('workspace_id', workspaceId)
    .eq('grade_id', gradeId)
    .maybeSingle();

  if (error) {
    if (/point_item_notes|does not exist|schema cache/i.test(error.message ?? '')) {
      return emptyPointItemNoteDoc(workspaceId, gradeId);
    }
    throw new Error(error.message);
  }

  if (!data) {
    const empty = emptyPointItemNoteDoc(workspaceId, gradeId);
    savePointItemNoteCache(empty);
    savePointItemNoteLoadedGroupIds(workspaceId, gradeId, []);
    return empty;
  }

  // payload의 memberPointsByGroup / rewardedLevels 스텁만 반영 — 테이블 이력은 그룹 lazy
  const settingsDoc = normalizePointItemNoteDoc(
    { ...(data.payload as object), updatedAt: data.updated_at },
    workspaceId,
    gradeId
  );
  // rewardedLevels 스텁만 남기고 테이블 이력 필드는 비움 (미로드 그룹과 구분)
  const stubByGroup: Record<string, GroupMemberPoints> = {};
  Object.entries(settingsDoc.memberPointsByGroup).forEach(([gid, g]) => {
    stubByGroup[gid] = {
      ...emptyGroupMemberPoints(),
      rewardedLevels: g.rewardedLevels ?? {},
    };
  });
  const doc: PointItemNoteDoc = {
    ...settingsDoc,
    memberPointsByGroup: stubByGroup,
  };
  savePointItemNoteCache(doc);
  savePointItemNoteLoadedGroupIds(workspaceId, gradeId, []);
  return doc;
}

export type EnsurePointItemNoteGroupOptions = {
  /**
   * 메인 탭이 프레젠용으로 워밍할 때:
   * - 필요 컬럼만 조회
   * - 프레젠에서 쓰지 않는 notes 생략
   * - loaded 메타는 남기지 않음(본 페이지가 이후 전체 이력을 다시 받을 수 있게)
   */
  presentationWarm?: boolean;
};

/**
 * 선택 그룹 멤버 이력을 DB에서 로드해 doc에 병합.
 * memberIds는 화면 레이아웃 기준(권장) — DB member_group_id 불일치로 조회가 비는 것을 방지.
 */
export async function ensurePointItemNoteGroup(
  workspaceId: string,
  gradeId: string,
  groupId: string,
  currentDoc?: PointItemNoteDoc | null,
  memberIds?: string[],
  options?: EnsurePointItemNoteGroupOptions
): Promise<PointItemNoteDoc> {
  if (!workspaceId || !gradeId || !groupId) {
    return currentDoc ?? emptyPointItemNoteDoc(workspaceId, gradeId);
  }

  const alreadyLoaded = loadPointItemNoteLoadedGroupIds(workspaceId, gradeId).includes(
    groupId
  );
  if (alreadyLoaded) {
    const shell =
      currentDoc ??
      loadPointItemNoteCache(workspaceId, gradeId) ??
      (await loadPointItemNoteSettingsDoc(workspaceId, gradeId));
    if (shell.memberPointsByGroup[groupId]) return shell;
  }

  /* 프레젠: 메인 탭 캐시만 — miss여도 REST 금지 (loaded 마킹도 하지 않음) */
  if (isMainPresentationBrowserTab() && !alreadyLoaded) {
    const syncCached =
      currentDoc ?? loadPointItemNoteCache(workspaceId, gradeId);
    if (syncCached?.memberPointsByGroup[groupId] != null) {
      return syncCached;
    }

    const bridged = await resolvePointItemNoteFromCacheOrMainTab(
      workspaceId,
      gradeId,
      groupId
    );
    if (bridged && bridged.memberPointsByGroup[groupId] != null) {
      return bridged;
    }
    console.warn('[메인프레젠·캐시] 포인트 그룹 캐시 미스', {
      workspaceId,
      gradeId,
      groupId,
    });
    const shell =
      syncCached ??
      bridged ??
      emptyPointItemNoteDoc(workspaceId, gradeId);
    if (shell.memberPointsByGroup[groupId] != null) return shell;
    return {
      ...shell,
      memberPointsByGroup: {
        ...shell.memberPointsByGroup,
        [groupId]: emptyGroupMemberPoints(),
      },
    };
  }

  const key = ensureGroupKey(workspaceId, gradeId, groupId);
  const existing = ensureGroupInflight.get(key);
  if (existing) return existing;

  const presentationWarm = options?.presentationWarm === true;
  const task = (async () => {
    const shell =
      currentDoc ??
      loadPointItemNoteCache(workspaceId, gradeId) ??
      (await loadPointItemNoteSettingsDoc(workspaceId, gradeId));

    const prev = shell.memberPointsByGroup[groupId];
    const loaded = await loadMemberPointsForGroup(
      workspaceId,
      gradeId,
      groupId,
      memberIds,
      presentationWarm
        ? { slimColumns: true, includeNotes: false }
        : undefined
    );
    const merged: GroupMemberPoints = {
      ...loaded,
      // presentationWarm은 notes를 생략 — 기존 캐시 notes는 유지
      notes: presentationWarm ? (prev?.notes ?? []) : loaded.notes,
      rewardedLevels: {
        ...(loaded.rewardedLevels ?? {}),
        ...(prev?.rewardedLevels ?? {}),
      },
    };

    const doc: PointItemNoteDoc = {
      ...shell,
      memberPointsByGroup: {
        ...shell.memberPointsByGroup,
        [groupId]: merged,
      },
    };
    savePointItemNoteCache(doc, { force: true });
    // presentationWarm은 부분 이력이므로 loaded 메타를 남기지 않음
    if (!presentationWarm) {
      markPointItemNoteGroupLoaded(workspaceId, gradeId, groupId, { force: true });
    }
    return doc;
  })().finally(() => {
    ensureGroupInflight.delete(key);
  });

  ensureGroupInflight.set(key, task);
  return task;
}

/**
 * 호환용: 설정 로드 후 캐시에 있던 로드 완료 그룹은 유지.
 * 멤버 이력은 학년 전체가 아니라 ensurePointItemNoteGroup으로 채운다.
 */
export async function getPointItemNote(
  workspaceId: string,
  gradeId: string
): Promise<PointItemNoteDoc> {
  return loadPointItemNoteSettingsDoc(workspaceId, gradeId);
}

/**
 * 과제 보상 지급용: 그룹 단위로 코인/아이템만 로드·저장.
 * 항상 DB에서 조회 — 빈 캐시로 DELETE+INSERT 하면 코인이 지워짐.
 */
export async function loadGroupPointsForAssignmentReward(
  workspaceId: string,
  gradeId: string,
  groupId: string
): Promise<GroupMemberPoints> {
  if (!workspaceId || !gradeId || !groupId) return emptyGroupMemberPoints();
  return loadMemberCoinAndItemsForGroup(workspaceId, gradeId, groupId);
}

export async function saveGroupAssignmentRewardMutations(
  workspaceId: string,
  gradeId: string,
  groupId: string,
  group: GroupMemberPoints,
  memberIds: string[]
): Promise<void> {
  if (!workspaceId || !gradeId || !groupId || memberIds.length === 0) return;
  await replaceMemberCoinAndItemsForMembers(workspaceId, gradeId, memberIds, group);

  const cached = loadPointItemNoteCache(workspaceId, gradeId);
  if (cached) {
    // 코인/아이템만 패치 — loaded 마킹하지 않음(부분 로드로 과제 이력이 비는 것 방지)
    const next = {
      ...cached,
      memberPointsByGroup: {
        ...cached.memberPointsByGroup,
        [groupId]: {
          ...(cached.memberPointsByGroup[groupId] ?? emptyGroupMemberPoints()),
          coinRecords: group.coinRecords,
          coinTotals: group.coinTotals,
          itemInstances: group.itemInstances,
        },
      },
      updatedAt: new Date().toISOString(),
    };
    savePointItemNoteCache(next, { force: true });
    publishPointItemNoteDoc(next);
  }

  await notifySchoolProgramMutation(workspaceId, ['pointNotes']);
}

/** 설정은 point_item_notes에, 멤버 이력은 지정 그룹 멤버만 테이블에 저장 */
export async function savePointItemNote(
  doc: PointItemNoteDoc,
  options?: { saveGroups?: PointItemNoteSaveGroup[] }
): Promise<void> {
  if (!doc.workspaceId || !doc.gradeId) return;
  const updatedAt = new Date().toISOString();
  const previouslyLoaded = loadPointItemNoteLoadedGroupIds(doc.workspaceId, doc.gradeId);

  const memberRewardedLevelsByGroup: Record<string, Record<string, number[]>> = {};
  Object.entries(doc.memberPointsByGroup).forEach(([gid, g]) => {
    if (g.rewardedLevels && Object.keys(g.rewardedLevels).length > 0) {
      memberRewardedLevelsByGroup[gid] = g.rewardedLevels;
    }
  });
  const payload = {
    pointSet: doc.pointSet,
    itemCards: doc.itemCards,
    itemCardImages: doc.itemCardImages ?? [],
    levelSettings: doc.levelSettings,
    classPointSettings: doc.classPointSettings ?? {
      enabled: false,
      integrateAllGroups: false,
      rewardTiers: [],
      grantedTierByGroup: {},
    },
    memberRewardedLevelsByGroup,
    updatedAt,
  };
  const row = {
    id: docId(doc.workspaceId, doc.gradeId),
    workspace_id: doc.workspaceId,
    grade_id: doc.gradeId,
    payload,
    updated_at: updatedAt,
  };

  const { error } = await supabase.from('point_item_notes').upsert(row, { onConflict: 'id' });
  if (error) {
    // 로컬만 저장하고 성공한 척하면 재로드 시 데이터가 사라짐 → 반드시 실패로 알림
    throw new Error(
      /point_item_notes|does not exist|schema cache/i.test(error.message ?? '')
        ? '포인트 저장 테이블이 없습니다. Supabase 마이그레이션(db push)을 적용한 뒤 다시 저장하세요.'
        : error.message
    );
  }

  const saveGroups = options?.saveGroups ?? [];
  try {
    for (const { groupId, memberIds } of saveGroups) {
      if (!groupId || memberIds.length === 0) continue;
      const group = doc.memberPointsByGroup[groupId] ?? emptyGroupMemberPoints();
      await replaceMemberPointDataForMembers(
        doc.workspaceId,
        doc.gradeId,
        memberIds,
        group
      );
    }
  } catch (e) {
    const msg = e instanceof Error ? e.message : String(e);
    // 테이블 미적용일 때만 레거시 JSON 폴백. 그 외(INSERT 실패 등)는 반드시 실패로 알림.
    // (DELETE 후 실패를 성공으로 처리하면 재로드 시 합산이 전부 0이 됨)
    if (!/does not exist|schema cache/i.test(msg)) {
      throw e instanceof Error ? e : new Error(msg);
    }
    const legacyPayload = {
      ...payload,
      memberPointsByGroup: doc.memberPointsByGroup,
    };
    const { error: legacyErr } = await supabase
      .from('point_item_notes')
      .upsert(
        { ...row, payload: legacyPayload },
        { onConflict: 'id' }
      );
    if (legacyErr) throw e instanceof Error ? e : new Error(msg);
  }

  await invalidateSchoolScopedDataCaches(doc.workspaceId, ['pointNotes']);

  // invalidate가 LS를 비우고 needsReload를 켜므로 force로 현재 문서·로드 그룹을 다시 캐시
  const finalLoaded = Array.from(
    new Set([
      ...previouslyLoaded,
      ...saveGroups.map((g) => g.groupId).filter(Boolean),
    ])
  );
  savePointItemNoteCache({ ...doc, updatedAt }, { force: true });
  savePointItemNoteLoadedGroupIds(doc.workspaceId, doc.gradeId, finalLoaded, {
    force: true,
  });
  publishPointItemNoteDoc({ ...doc, updatedAt }, finalLoaded);
}
